import { z } from 'zod';
import { shareLinkSchema } from './share.js';
/**
 * Contrats de stockage (XFILE-01, -02) — source unique front + back.
 *
 * Arborescence de dossiers PUREMENT LOGIQUES (cf. `0003_storage.sql`) : navigation,
 * création / renommage / suppression de dossiers, listing d'un dossier (sous-dossiers
 * + fichiers) et rangement par défaut année/mois `AAAA/MM`. Le contenu des fichiers et
 * l'upload arrivent en T4 ; ici les fichiers sont des métadonnées listables.
 */
/**
 * Origine d'un dossier : `user` (créé à la main, modifiable) ou `auto` (rangement
 * année/mois géré par le système, non modifiable à la main). Aligné sur l'enum
 * PostgreSQL `folder_source`.
 */
export const folderSourceSchema = z.enum(['user', 'auto']);
/**
 * ESPACE de stockage d'un dossier (XFILE-25) — aligné sur l'enum PostgreSQL `folder_space`
 * (`0016_spaces.sql`). Le stockage est structuré en QUATRE espaces de premier niveau, chacun
 * matérialisé par un DOSSIER RACINE (`parent_id IS NULL`) :
 *  - `public` : espace partagé « wiki » — lecture ET écriture (dépôt, création/renommage/
 *               déplacement/suppression) ouvertes à TOUT utilisateur authentifié ;
 *  - `auto`   : rangement automatique `AAAA/MM` des dépôts WEB — dépôt ouvert à tout authentifié
 *               (+ dépôt communautaire), création manuelle de dossier INTERDITE, gestion admin ;
 *  - `api`    : idem `auto` mais pour les dépôts API (provenance `pnyx`) — écrit UNIQUEMENT par
 *               l'API, gestion admin ;
 *  - `user`   : espace PERSONNEL privé, UNE racine PAR compte (`owner_id`) — visible/gérable par
 *               son PROPRIÉTAIRE (tout rôle) ou un admin ; INVISIBLE aux autres non-admins.
 */
export const folderSpaceSchema = z.enum(['public', 'auto', 'api', 'user']);
/**
 * Encode la MATRICE de droits par espace (XFILE-25) — SOURCE UNIQUE front + back. Helper PUR et
 * DÉTERMINISTE (aucun I/O). Un utilisateur `null` (anonyme) n'a aucune capacité (le dépôt anonyme
 * communautaire passe par un chemin dédié côté serveur, hors de ce helper).
 *
 * Matrice :
 *  - `public` : lecture/dépôt/création ouverts à TOUT authentifié ; gestion ouverte à tout authentifié
 *               SAUF la racine (immuable) ;
 *  - `auto`   : lecture/dépôt ouverts à tout authentifié ; création de dossier INTERDITE à tous
 *               (système only) ; gestion réservée aux admins (hors racine) ;
 *  - `api`    : lecture ouverte à tous ; AUCUN dépôt web (écrit uniquement par l'API) ; création
 *               interdite ; gestion admin (hors racine) ;
 *  - `user`   : lecture/dépôt/création/gestion réservés au PROPRIÉTAIRE ou à un admin ; INVISIBLE
 *               aux autres (toutes capacités `false`). La racine perso reste immuable (`canManage`).
 */
export function folderCapabilities(input, user) {
    const none = {
        canRead: false,
        canUpload: false,
        canCreateFolder: false,
        canManage: false,
    };
    if (user === null)
        return none;
    const isAdmin = user.role === 'admin' || user.role === 'superadmin';
    const notRoot = !input.isSpaceRoot;
    switch (input.space) {
        case 'public':
            return { canRead: true, canUpload: true, canCreateFolder: true, canManage: notRoot };
        case 'auto':
            return { canRead: true, canUpload: true, canCreateFolder: false, canManage: isAdmin && notRoot };
        case 'api':
            return { canRead: true, canUpload: false, canCreateFolder: false, canManage: isAdmin && notRoot };
        case 'user': {
            const allowed = isAdmin || (input.ownerId !== null && input.ownerId === user.id);
            return {
                canRead: allowed,
                canUpload: allowed,
                canCreateFolder: allowed,
                canManage: allowed && notRoot,
            };
        }
    }
}
/**
 * CAPACITÉS sur un FICHIER (XFILE-73) — même matrice que {@link folderCapabilities}, appliquée à la
 * méta du dossier CONTENANT, mais avec `isSpaceRoot` FORCÉ À `false`.
 *
 * POURQUOI CE HELPER EXISTE, alors qu'il tient en une ligne. `folderCapabilities` rend
 * `canManage: false` sur toute racine d'espace, pour tout rôle, superadmin compris. Ce refus protège
 * LA RACINE ELLE-MÊME (on ne renomme, ne déplace ni n'efface la structure), et il ne dit
 * ABSOLUMENT RIEN du droit d'agir sur ce qu'elle CONTIENT. Un fichier n'est jamais une racine
 * d'espace : lui appliquer le `canManage` de sa racine, c'est répondre à une question qu'on n'a pas
 * posée.
 *
 * ⚠ SE TROMPER DE HELPER EST LE DÉFAUT XFILE-73, et il a été livré. Le serveur avait la distinction
 * juste — il recalculait `folderCapabilities({ ...meta, isSpaceRoot: false })` à la main sur TROIS
 * sites, chacun avec son propre commentaire. Le client web, lui, a lu `canManage` de la racine et en
 * a conclu que les fichiers posés DIRECTEMENT à la racine d'un espace n'étaient ni supprimables ni
 * déplaçables — pour personne, superadmin compris — alors que l'API les acceptait. Une règle
 * réimplémentée trois fois côté serveur et zéro fois côté client n'est pas une source unique : d'où
 * ce helper, qui rend l'erreur impossible à refaire en la retirant du domaine de ce qu'on recopie.
 *
 * ⚠ ON PASSE LA MÉTA DU DOSSIER CONTENANT, jamais celle du fichier (un fichier n'a ni espace ni
 * propriétaire propres — il les hérite de son dossier). `isSpaceRoot` de l'entrée est IGNORÉ, ce qui
 * est précisément le service rendu : l'appelant n'a plus à se demander s'il doit le neutraliser.
 *
 * Le reste de la matrice s'applique INCHANGÉ : un fichier dans l'espace personnel d'autrui reste
 * invisible (toutes capacités `false`), un fichier d'`auto`/`api` reste géré par les seuls admins.
 */
export function fileCapabilities(input, user) {
    return folderCapabilities({ ...input, isSpaceRoot: false }, user);
}
/** Un caractère est-il interdit dans un nom d'entrée (séparateur de chemin ou contrôle) ? */
function isForbiddenNameChar(ch) {
    const code = ch.charCodeAt(0);
    return ch === '/' || ch === '\\' || code < 0x20 || code === 0x7f;
}
/**
 * Nom de dossier/fichier VALIDE : 1..255 caractères après trim, sans séparateur de
 * chemin (`/` `\`) ni caractère de contrôle, et jamais `.`/`..` (noms réservés du
 * système de fichiers). Espaces internes, tirets, points et Unicode restent autorisés.
 * Validation par `.refine` (revalidée côté handler via `safeParse`).
 */
export const entryNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(255)
    .refine((n) => ![...n].some(isForbiddenNameChar), 'Nom invalide (séparateur de chemin ou caractère de contrôle)')
    .refine((n) => n !== '.' && n !== '..', 'Nom réservé');
/**
 * NOM D'AFFICHAGE d'un compte, contraint comme un NOM DE DOSSIER (XFILE-25).
 *
 * POURQUOI CE SCHÉMA VIT ICI, à côté d'{@link entryNameSchema}, et pas dans les contrats de
 * compte : depuis les espaces (v2.0.0), le `display_name` d'un compte N'EST PLUS UNE SIMPLE
 * ÉTIQUETTE — `ensureUserRoot` en fait le NOM du dossier racine de l'espace personnel. Il devient
 * donc un segment de chemin, et tout le code aval repose sur l'invariant d'{@link entryNameSchema} :
 * chemins relatifs des archives ZIP, arborescence nommée d'`xfile export` (ADR-0013), fils d'Ariane.
 * Un compte nommé `../../etc` rompt cet invariant à la source. Deux filets le rattrapent aujourd'hui
 * (l'assainissement `safeSegment()` de l'export, et le fait que le ZIP recompose les segments), mais
 * un invariant qui ne tient que par ses filets n'est pas un invariant.
 *
 * BORNE À 100, pas 255 : on conserve la borne historique de `createUserRequestSchema` (un nom
 * d'affichage n'est pas un nom de fichier long), on n'AJOUTE que les interdits de chemin.
 *
 * ⚠ À N'UTILISER QUE SUR LES CHEMINS D'ÉCRITURE (création/mise à jour d'un compte). Les schémas de
 * LECTURE (`adminUserSchema.displayName`, `authUserSchema.displayName`) restent volontairement des
 * `z.string()` permissifs : la base peut contenir des comptes ANTÉRIEURS non conformes, et resserrer
 * la lecture ferait échouer le `parse` du listing sur une donnée déjà stockée — on refuse d'en créer
 * de nouveaux sans rendre les anciens illisibles. La normalisation des comptes existants, si elle est
 * un jour souhaitée, est une migration de données à part entière.
 */
export const displayNameSchema = entryNameSchema.max(100);
/**
 * AGRÉGAT de durée de vie (TTL) d'un SOUS-ARBRE de fichiers ACTIFS (Lot B), calculé
 * RÉCURSIVEMENT sur tout le sous-arbre :
 *  - `hasForever`   : au moins un fichier descendant actif est ÉTERNEL (`expires_at IS NULL`) ;
 *  - `minExpiresAt` : plus PROCHE échéance finie = MIN des `expires_at` non nuls (`null` si aucune) —
 *                     c'est la PROCHAINE disparition du sous-arbre ;
 *  - `maxExpiresAt` : plus LOINTAINE échéance finie = MAX des `expires_at` non nuls (`null` si aucune).
 * Dates ISO. Un sous-arbre SANS fichier actif → `{ minExpiresAt: null, maxExpiresAt: null, hasForever: false }`.
 */
export const ttlAggregateSchema = z.object({
    minExpiresAt: z.string().nullable(),
    maxExpiresAt: z.string().nullable(),
    hasForever: z.boolean(),
});
/**
 * Un sous-dossier tel que listé dans son parent. Les trois champs d'AGRÉGAT TTL du sous-arbre
 * (Lot B) sont OPTIONNELS : ils sont remplis dans un LISTING (`folderListingSchema.folders`) mais
 * ABSENTS sur un nœud renvoyé par create/rename/move (le web recharge le listing après mutation).
 * Sous-arbre sans fichier actif → `minExpiresAt`/`maxExpiresAt` `null`, `hasForever` `false`.
 */
export const folderNodeSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    source: folderSourceSchema,
    createdAt: z.string(),
    minExpiresAt: z.string().nullable().optional(),
    maxExpiresAt: z.string().nullable().optional(),
    hasForever: z.boolean().optional(),
    /**
     * ESPACE du dossier (XFILE-25) et PROPRIÉTAIRE (`ownerId`, non nul seulement dans l'espace
     * `user`). OPTIONNELS pour rester ADDITIFS (le web déjà déployé continue de typechecker) : l'API
     * les renseigne systématiquement dans les listings et les nœuds renvoyés par create/rename/move.
     */
    space: folderSpaceSchema.optional(),
    ownerId: z.uuid().nullable().optional(),
});
/**
 * Classe de DURÉE DE VIE (TTL) d'un fichier, choisie « en un clic » à l'upload
 * (XFILE-03). `forever` ne périme jamais (pas d'`expires_at`). Aligné sur l'enum
 * PostgreSQL `file_ttl_kind` (`0004_files_ttl.sql`). Défaut applicatif : `1m` (1 mois).
 */
export const ttlKindSchema = z.enum(['1d', '1w', '1m', '1y', '5y', 'forever']);
/** Les classes de TTL, dans l'ordre d'affichage (du plus court à « toujours »). */
export const TTL_KINDS = ttlKindSchema.options;
/** TTL par défaut appliqué si l'appelant n'en choisit pas (XFILE-03). */
export const DEFAULT_TTL_KIND = '1m';
/**
 * Classes de TTL que le SUPERADMIN peut activer/désactiver par cases à cocher (console →
 * Paramètres) : les seules exposables « en un clic » dans les écrans de dépôt. Liste BLANCHE
 * (le réglage `uploadTtlKinds` doit en être un sous-ensemble non vide). `1w` reste une valeur
 * d'enum VALIDE en base/entrée mais n'est pas proposée ici. Ordre = affichage (croissant).
 */
export const SELECTABLE_UPLOAD_TTL_KINDS = [
    '1d',
    '1m',
    '1y',
    '5y',
    'forever',
];
/** Sous-enum des classes de TTL sélectionnables (validation du réglage `uploadTtlKinds`). */
export const selectableTtlKindSchema = z.enum(SELECTABLE_UPLOAD_TTL_KINDS);
/**
 * Durées proposées par DÉFAUT à l'upload tant que le superadmin n'a rien changé : 30 jours
 * (`1m`) + éternel (`forever`). Aligné sur le défaut SQL de `app_settings.upload_ttl_kinds`
 * (`0015`). Ceci ne borne QUE l'interface — le backend accepte toujours l'intégralité de
 * `TTL_KINDS` en entrée. Rien n'est pré-sélectionné tant que `defaultTtlKind` vaut `null`.
 */
export const DEFAULT_UPLOAD_TTL_KINDS = ['1m', 'forever'];
/**
 * Calcule l'instant d'expiration d'un fichier à partir de sa classe de TTL et d'une
 * date de référence (`now`, injectable pour la testabilité — TESTS §4). Renvoie `null`
 * pour `forever` (jamais d'expiration). Arithmétique CALENDAIRE en UTC : `1m` = même
 * quantième le mois suivant, `1y`/`5y` idem en années (le débordement de fin de mois
 * suit la sémantique JS, ex. 31 janv. + 1 mois → 3 mars — acceptable pour un TTL).
 * Helper PUR (source unique front + back) : la pose effective de `expires_at` en base
 * se fait côté serveur à l'upload.
 */
export function expiresAtFor(kind, now) {
    if (kind === 'forever')
        return null;
    const d = new Date(now.getTime());
    switch (kind) {
        case '1d':
            d.setUTCDate(d.getUTCDate() + 1);
            break;
        case '1w':
            d.setUTCDate(d.getUTCDate() + 7);
            break;
        case '1m':
            d.setUTCMonth(d.getUTCMonth() + 1);
            break;
        case '1y':
            d.setUTCFullYear(d.getUTCFullYear() + 1);
            break;
        case '5y':
            d.setUTCFullYear(d.getUTCFullYear() + 5);
            break;
    }
    return d;
}
/**
 * PROVENANCE d'un fichier (intégration pnyx ; cf. `../commons/contrats/xfile-pnyx-api.md`) :
 *  - `web`  : dépôt direct via l'interface web de xfile ;
 *  - `pnyx` : upload via l'API, depuis pnyx (upload proxifié).
 * DÉTERMINÉE PAR LE SERVEUR au dépôt (jamais fournie par le client) : le deposit-token porte
 * la provenance, l'upload web est toujours `web`. Extensible (une autre app de la famille
 * aurait sa propre valeur) ; `web`/`pnyx` sont le périmètre actuel. Aligné sur l'enum
 * PostgreSQL `file_provenance` (`0011_pnyx_integration.sql`).
 */
export const provenanceSchema = z.enum(['web', 'pnyx']);
/** Provenance par défaut : `web` (préserve le comportement de l'interface web). */
export const DEFAULT_PROVENANCE = 'web';
/**
 * Un fichier tel que listé dans son dossier. `hasContent` = le contenu est présent
 * sur disque (clé de stockage posée à l'upload, effacée par la GC à la purge) ; `false`
 * tant que l'upload n'a rien écrit OU après purge. `expiresAt` = échéance TTL ISO
 * (`null` si `forever`). La clé de stockage interne n'est JAMAIS exposée.
 */
export const fileNodeSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    mimeType: z.string(),
    hasContent: z.boolean(),
    ttlKind: ttlKindSchema,
    expiresAt: z.string().nullable(),
    createdAt: z.string(),
    /** PROVENANCE du dépôt (`web` / `pnyx`) — affichée dans les propriétés du fichier (T13). */
    provenance: provenanceSchema,
    /** Nombre de CONSULTATIONS (services complets des octets, HTTP 200) — T13. */
    consultationCount: z.number(),
    /** Date ISO de dernière consultation, ou `null` si jamais consulté (T13). */
    lastConsultedAt: z.string().nullable(),
    /**
     * Une VIGNETTE dérivée a-t-elle été générée pour ce fichier (colonne `files.has_thumbnail`) ?
     * XFILE-26. OPTIONNEL (additif, garde `apps/web` vert) : l'API le renseigne dans les LISTINGS de
     * dossier. Le web l'utilise pour décider l'inclusion en GALERIE — une image apparaît ssi elle est
     * nativement affichable (jpeg/png/gif/webp/avif/svg) OU pourvue d'une vignette. Ainsi un HEIC
     * décodé en WebP apparaît (vignette présente), un HEIC non décodable est exclu proprement.
     */
    hasThumbnail: z.boolean().optional(),
});
/** Extension (sans point, en minuscules) d'un nom de fichier, ou `null` si absente. */
function extensionOf(name) {
    const dot = name.lastIndexOf('.');
    if (dot < 0 || dot === name.length - 1)
        return null;
    return name.slice(dot + 1).toLowerCase();
}
/** Catégorie déduite d'une extension connue (repli quand le MIME n'est pas concluant). */
const CATEGORY_BY_EXTENSION = {
    pdf: 'pdf',
    doc: 'word',
    docx: 'word',
    xls: 'excel',
    xlsx: 'excel',
    csv: 'excel',
    ppt: 'powerpoint',
    pptx: 'powerpoint',
    zip: 'archive',
    rar: 'archive',
    '7z': 'archive',
    tar: 'archive',
    gz: 'archive',
    png: 'image',
    jpg: 'image',
    jpeg: 'image',
    gif: 'image',
    webp: 'image',
    svg: 'image',
    avif: 'image',
    heic: 'image',
    heif: 'image',
    mp4: 'video',
    mov: 'video',
    webm: 'video',
    mkv: 'video',
    avi: 'video',
    mp3: 'audio',
    wav: 'audio',
    ogg: 'audio',
    flac: 'audio',
    m4a: 'audio',
    txt: 'text',
    md: 'text',
    json: 'text',
    xml: 'text',
    log: 'text',
};
/**
 * Classe un fichier en `FileCategory` à partir de son type MIME (prioritaire) puis, à
 * défaut, de l'EXTENSION de son nom. Helper PUR et DÉTERMINISTE (source unique front +
 * back) : aucun I/O, aucune dépendance à l'environnement. Renvoie `other` si rien ne
 * correspond.
 */
export function fileCategory(mimeType, name) {
    const mime = mimeType.toLowerCase();
    if (mime.startsWith('image/'))
        return 'image';
    if (mime.startsWith('video/'))
        return 'video';
    if (mime.startsWith('audio/'))
        return 'audio';
    if (mime === 'application/pdf')
        return 'pdf';
    if (mime.includes('word') ||
        mime.includes('msword') ||
        mime.includes('officedocument.wordprocessingml'))
        return 'word';
    if (mime.includes('excel') || mime.includes('spreadsheetml') || mime.includes('ms-excel'))
        return 'excel';
    if (mime.includes('powerpoint') || mime.includes('presentationml'))
        return 'powerpoint';
    if (mime.includes('zip') ||
        mime.includes('rar') ||
        mime.includes('7z') ||
        mime.includes('tar') ||
        mime.includes('gzip') ||
        mime.includes('x-compressed'))
        return 'archive';
    if (mime.startsWith('text/'))
        return 'text';
    const ext = name ? extensionOf(name) : null;
    const byExt = ext ? CATEGORY_BY_EXTENSION[ext] : undefined;
    return byExt ?? 'other';
}
/**
 * Le contenu peut-il être rendu INLINE dans le navigateur (média brut) ? Vrai ssi le
 * type MIME commence par `image/` ou `video/`. Utilisé par le serveur pour renvoyer un
 * média brut inline sur `/f/:slug` et par l'aperçu web.
 */
export function isInlineViewable(mimeType) {
    const mime = mimeType.toLowerCase();
    return mime.startsWith('image/') || mime.startsWith('video/');
}
/**
 * Le contenu peut-il être PRÉVISUALISÉ dans la visionneuse de dossier (XFILE-20) ? Vrai
 * ssi image (GIF/animées incluses) ou vidéo — identique à `isInlineViewable` (le média brut
 * se rend directement). Le PDF N'EST PLUS prévisualisé en interne (T13) : comme tout autre
 * type (bureautique, archive, texte, binaire…) il retombe sur le panneau « non
 * prévisualisable », qui propose « Ouvrir dans un nouvel onglet » (Chrome affiche le PDF) et
 * « Télécharger ». Helper PUR (source unique front + back).
 */
export function isPreviewable(mimeType) {
    const mime = mimeType.toLowerCase();
    return mime.startsWith('image/') || mime.startsWith('video/');
}
/** Un maillon du fil d'Ariane (racine → dossier courant, dossier courant inclus). */
export const breadcrumbItemSchema = z.object({
    id: z.uuid(),
    name: z.string(),
});
/**
 * Réponse du listing d'un dossier : le dossier courant (`null` à la racine), le fil
 * d'Ariane (vide à la racine), puis ses sous-dossiers et fichiers actifs.
 */
export const folderListingSchema = z.object({
    folderId: z.uuid().nullable(),
    source: folderSourceSchema.nullable(),
    breadcrumb: z.array(breadcrumbItemSchema),
    folders: z.array(folderNodeSchema),
    files: z.array(fileNodeSchema),
    /**
     * AGRÉGAT TTL du DOSSIER COURANT (Lot B) — agrégé sur TOUT son sous-arbre de fichiers actifs.
     * ABSENT à la racine (`folderId === null`), présent pour un dossier donné (même vide → tous champs
     * neutres). Complète les agrégats par sous-dossier portés par `folders[].{min,max}ExpiresAt/hasForever`.
     */
    ttl: ttlAggregateSchema.optional(),
    /**
     * ESPACE / PROPRIÉTAIRE du DOSSIER COURANT (XFILE-25) — OPTIONNELS (additifs). `space` = l'espace
     * du dossier listé (absent à la racine virtuelle) ; `ownerId`/`ownerName` = propriétaire de l'espace
     * `user` (null hors `user`). Renseignés par l'API dans un listing de dossier donné.
     */
    space: folderSpaceSchema.optional(),
    ownerId: z.uuid().nullable().optional(),
    ownerName: z.string().nullable().optional(),
});
/**
 * RESTRICTION du listing (`GET /api/folders[/:id]?only=…`, XFILE-57) — demande explicitement au
 * serveur de NE PAS ASSEMBLER une partie de la réponse.
 *
 * POURQUOI. `FolderPicker` (sélecteur de destination de « Déplacer ») navigue l'arbre avec le MÊME
 * endpoint que l'explorateur, mais ne consomme que `folders` et `breadcrumb`. Ouvrir « Déplacer »
 * depuis un dossier de 500 fichiers transférait donc 500 enregistrements complets (taille, MIME,
 * provenance, compteurs de consultation…) ET déclenchait DEUX CTE RÉCURSIVES d'agrégat TTL
 * (`childrenSubtreeTtl` + `subtreeTtl`, dont le coût est proportionnel au SOUS-ARBRE, pas au
 * dossier), pour peupler une liste de noms de dossiers. Ce n'est pas un filtrage de la réponse : le
 * serveur SAUTE réellement ces lectures.
 *
 * ⚠ ENUM, PAS BOOLÉEN, et c'est délibéré. `z.coerce.boolean()` est `Boolean(v)` : sur une
 * query-string, `?foldersOnly=false` vaut `true` — un flag booléen coercé accepte donc en silence
 * l'exact contraire de ce qu'on lui demande. Une liste blanche à un seul membre REFUSE (400) toute
 * valeur qu'elle ne connaît pas, et laisse la place à un `only=files` ultérieur sans réécrire le
 * contrat.
 *
 * ⚠ ADDITIF au sens strict. La forme de la réponse ne change PAS ({@link folderListingSchema} est
 * inchangé) : `files` est rendu VIDE plutôt que retiré — un consommateur qui itère dessus sans
 * demander `only` n'est pas concerné, et celui qui le demande a déclaré ne pas s'en servir. `ttl`
 * est déjà optionnel, donc simplement ABSENT.
 *
 * ⚠ Les trois champs d'agrégat TTL de {@link folderNodeSchema} (`minExpiresAt`, `maxExpiresAt`,
 * `hasForever`) sont eux aussi ABSENTS des sous-dossiers en mode `only=folders` — jamais remplis
 * d'une valeur neutre. `hasForever: false` sur un sous-arbre non calculé serait une AFFIRMATION
 * FAUSSE (« aucun fichier éternel là-dedans ») là où l'absence dit correctement « non calculé ».
 * Ces champs sont optionnels précisément pour ça.
 *
 * ⚠ N'ALLÈGE AUCUNE GARDE. Le contrôle de visibilité (404 sur l'espace personnel d'autrui) précède
 * le listing et est rigoureusement le même dans les deux modes : un paramètre de requête choisit ce
 * qu'on assemble, jamais ce qu'on a le droit de voir.
 */
export const listingOnlySchema = z.enum(['folders']);
/** Query de `GET /api/folders` et `GET /api/folders/:id`. ABSENT ⇒ listing COMPLET (inchangé). */
export const folderListingQuerySchema = z.object({
    only: listingOnlySchema.optional(),
});
/**
 * RÉSUMÉ d'un espace visible par l'appelant (`GET /api/spaces`, XFILE-25) : l'espace, l'id de sa
 * RACINE (à ouvrir pour naviguer dedans), et son nom. Pour l'espace `user`, `ownerId`/`ownerName`
 * identifient le compte (utile à un admin qui voit aussi les espaces `user` des autres comptes).
 */
export const spaceSummarySchema = z.object({
    space: folderSpaceSchema,
    folderId: z.uuid(),
    name: z.string(),
    ownerId: z.uuid().nullable().optional(),
    ownerName: z.string().nullable().optional(),
});
/**
 * Réponse de `GET /api/spaces` : la liste des espaces visibles par l'appelant (public/auto/api +
 * sa racine perso ; un admin voit AUSSI les racines `user` des autres comptes actifs). Alimente la
 * future page d'accueil web.
 */
export const spacesResponseSchema = z.array(spaceSummarySchema);
/**
 * Réponse de `GET /api/folders/:id/archive/estimate` (Lot C) : ESTIMATION du contenu qu'un
 * téléchargement ZIP du dossier produirait, AGRÉGÉE RÉCURSIVEMENT sur tout le sous-arbre rooté
 * sur `:id`. Ne compte QUE les fichiers ACTIFS AVEC CONTENU (`storage_key IS NOT NULL`) :
 *  - `count`      : nombre de fichiers qui seraient inclus dans l'archive ;
 *  - `totalBytes` : somme de leurs tailles (octets — total NON compressé, l'archive étant en
 *                   mode STORED donc de taille ≈ `totalBytes` + surcharge d'en-têtes ZIP).
 * Sert à prévenir l'utilisateur du volume avant de lancer le téléchargement. Sous-arbre sans
 * fichier avec contenu → `{ count: 0, totalBytes: 0 }`.
 */
export const archiveEstimateSchema = z.object({
    count: z.number().int().nonnegative(),
    totalBytes: z.number().int().nonnegative(),
});
/**
 * Corps de `POST /api/folders/:id/extend-ttl` et `POST /api/files/:id/extend-ttl` (Lot B) :
 * PROLONGE (PLANCHER SEULEMENT) la conservation des fichiers actifs visés à la classe `ttlKind`.
 * OUVERT À TOUT UTILISATEUR AUTHENTIFIÉ (opération NON destructive : ne peut QU'ALLONGER la vie,
 * jamais la raccourcir). Accepte TOUTE classe de `ttlKindSchema` (`forever` inclus).
 */
export const extendTtlRequestSchema = z.object({ ttlKind: ttlKindSchema });
/** Réponse d'`extend-ttl` : nombre de fichiers RÉELLEMENT prolongés (échéance effectivement repoussée). */
export const extendTtlResultSchema = z.object({ extended: z.number().int().nonnegative() });
/**
 * Réponse de `GET /api/folders/:id/delete-impact` (XFILE-33) : ce qu'un `DELETE /api/folders/:id`
 * DÉTRUIRAIT, agrégé RÉCURSIVEMENT sur le sous-arbre — de quoi énoncer l'impact AVANT d'agir :
 *  - `folders` : nombre de SOUS-DOSSIERS actifs descendants (le dossier lui-même N'EST PAS compté) ;
 *  - `depth`   : profondeur MAXIMALE des sous-dossiers sous le dossier (`0` s'il n'en a aucun) ;
 *  - `files`   : nombre de fichiers ACTIFS de TOUT le sous-arbre, dossier courant INCLUS ;
 *  - `ttl`     : agrégat de durée de vie de ces MÊMES fichiers ({@link ttlAggregateSchema}).
 *
 * ⚠ Contrairement à {@link archiveEstimateSchema}, `files` ne filtre PAS sur la présence du
 * contenu (`storage_key`) : on annonce ce qui sera EFFACÉ, pas ce qui serait zippable — un fichier
 * dont le binaire a déjà été purgé disparaît quand même de l'arborescence.
 */
export const folderDeleteImpactSchema = z.object({
    folders: z.number().int().nonnegative(),
    depth: z.number().int().nonnegative(),
    files: z.number().int().nonnegative(),
    ttl: ttlAggregateSchema,
});
/**
 * Réponse des suppressions ANNULABLES (XFILE-33) — `DELETE /api/folders/:id` et
 * `DELETE /api/files/:id`, qui renvoyaient jusqu'ici `204 No Content` et passent à `200` avec ce
 * corps. `batch` est l'ESTAMPILLE du lot : l'uuid posé sur les SEULES lignes que l'opération a
 * fait basculer (`deleted_at IS NULL` au moment du `UPDATE`). Le client le rejoue tel quel dans
 * `POST /api/deletions/:batch/restore` pour annuler EXACTEMENT cette suppression — jamais ce qui
 * avait été effacé avant et indépendamment.
 */
export const deletionBatchSchema = z.object({ batch: z.uuid() });
/**
 * Réponse de `POST /api/deletions/:batch/restore` (XFILE-33) : le nombre de lignes RÉELLEMENT
 * ressuscitées, par nature. Un lot INCONNU (jamais émis, déjà restauré, ou purgé définitivement)
 * ne renvoie pas ce corps mais un 404/410 — la restauration n'est donc pas « idempotente à 200 » :
 * la rejouer signale que le lot n'existe plus (cf. la route pour le détail des codes).
 */
export const restoreBatchResultSchema = z.object({
    folders: z.number().int().nonnegative(),
    files: z.number().int().nonnegative(),
    /**
     * Fichiers du lot NON restaurés parce que leur CONTENU avait déjà été purgé (audit v2.4.0,
     * constat 2 du lot 2). OPTIONNEL, donc additif ; absent ⇔ `0`.
     *
     * ⚠ `> 0` ⇒ SUCCÈS PARTIEL, et c'est une information que l'interface DOIT dire. Le reste du lot
     * est bien revenu, mais ces fichiers-là ne reviendront jamais : la GC avait relâché leur référence
     * de dédup et unlinké le blob. Les ressusciter aurait donné des lignes actives sans octets —
     * visibles dans le listing, intéléchargeables. Ce n'est PAS un 409 : l'opération a réussi, le lot
     * est consommé, et la rejouer répondrait 404.
     *
     * ⚠ LOTS WEB (5-8) : un toaster « Annulé » qui masquerait ce compteur rétablirait exactement le
     * mensonge que ce correctif supprime. Il faut une phrase du type « N fichier(s) n'ont pas pu être
     * restaurés : leur contenu avait déjà été effacé ».
     */
    purgedFiles: z.number().int().nonnegative().optional(),
});
/**
 * Corps de `POST /api/deletions/:batch/restore-to` (XFILE-75) : restaure un lot de suppression
 * MANUELLE vers un dossier de destination CHOISI (`destFolderId`), et non à son emplacement
 * d'origine. Les RACINES du lot y atterrissent (dédup de nom sur collision), leur sous-arbre suit et
 * hérite de l'espace de la destination, chaque FICHIER repart avec un TTL PLANCHER de 24 h, et les
 * liens de partage des éléments restaurés sont RÉVOQUÉS (remettre en ligne = re-partager).
 *
 * ⚠ DISTINCTE de `POST /api/deletions/:batch/restore` (sans corps), qui reste l'undo IMMÉDIAT du
 * toaster (« Annuler »), à l'ORIGINE, ouvert à tout authentifié. Celle-ci est réservée aux admins et
 * choisit toujours OÙ restaurer. La réponse réutilise {@link restoreBatchResultSchema}.
 */
export const restoreBatchToDestinationRequestSchema = z.object({
    destFolderId: z.uuid(),
});
/**
 * Corps de `POST /api/folders` : crée un dossier `user` sous `parentId` (`null` =
 * racine). Toujours `source: 'user'` — les dossiers `auto` sont créés par le système.
 */
export const createFolderRequestSchema = z.object({
    parentId: z.uuid().nullable(),
    name: entryNameSchema,
});
/** Corps de `PATCH /api/folders/:id` : renomme un dossier `user`. */
export const renameFolderRequestSchema = z.object({
    name: entryNameSchema,
});
/**
 * Corps de `PATCH /api/folders/:id` (admin) : RENOMME et/ou DÉPLACE un dossier `user`. Les deux
 * champs sont optionnels et INDÉPENDANTS — au moins UN doit être fourni (le serveur renvoie 400
 * sinon) :
 *  - `name` présent → RENOMMAGE (mêmes règles que `renameFolderRequestSchema`) ;
 *  - `parentId` présent → DÉPLACEMENT sous ce parent. `null` = déplacement vers la RACINE ;
 *    ABSENT = pas de déplacement (distinction absent / `null` faite côté serveur : la clé
 *    absente vaut `undefined`, la clé à `null` vaut « racine »).
 * Le déplacement ne casse AUCUN lien de partage (les `share_links` référencent l'id, jamais le
 * chemin). `renameFolderRequestSchema` reste le contrat de compat pour le renommage seul.
 */
export const updateFolderRequestSchema = z.object({
    name: entryNameSchema.optional(),
    parentId: z.uuid().nullable().optional(),
});
/**
 * Borne raisonnable du nombre d'ids d'une opération GROUPÉE sur fichiers (garde-fou anti-abus,
 * une requête n'agit jamais sur un lot démesuré). Partagée par `bulk-delete` et `bulk-move`.
 */
export const BULK_FILES_MAX = 1000;
/**
 * Corps de `POST /api/files/bulk-delete` (admin) : soft-delete d'un LOT de fichiers, désignés par
 * leurs ids internes (1..{@link BULK_FILES_MAX}). Le contenu disque reste purgé plus tard par la
 * GC / la purge superadmin (le soft-delete ne décrémente pas le refcount de dédup) — même
 * comportement que la suppression d'un fichier unique.
 */
export const bulkDeleteFilesRequestSchema = z.object({
    ids: z.array(z.uuid()).min(1).max(BULK_FILES_MAX),
});
/**
 * Réponse de `POST /api/files/bulk-delete` : nombre de fichiers RÉELLEMENT soft-deletés, plus
 * l'ESTAMPILLE du lot (XFILE-33). `batch` est OPTIONNEL — additif, donc les clients antérieurs
 * continuent de typechecker — et ABSENT quand `deleted === 0` (aucune ligne n'a basculé, il n'y a
 * rien à annuler). Présent, il se rejoue dans `POST /api/deletions/:batch/restore`.
 */
export const bulkDeleteFilesResultSchema = z.object({
    deleted: z.number().int().nonnegative(),
    batch: z.uuid().optional(),
});
/**
 * Corps de `POST /api/files/bulk-move` (admin) : DÉPLACE un LOT de fichiers vers `folderId`. Le
 * dossier de destination doit exister et être ACTIF (404 sinon). Les liens de partage restent
 * valides (l'id du fichier ne change pas, seul `folder_id` bouge).
 */
export const bulkMoveFilesRequestSchema = z.object({
    ids: z.array(z.uuid()).min(1).max(BULK_FILES_MAX),
    folderId: z.uuid(),
});
/** Réponse de `POST /api/files/bulk-move` : nombre de fichiers ACTIFS effectivement déplacés. */
export const bulkMoveFilesResultSchema = z.object({
    moved: z.number().int().nonnegative(),
});
/**
 * Borne HISTORIQUE du nombre de DOSSIERS d'une archive de SÉLECTION (XFILE-27).
 *
 * ⚠ N'EST PLUS CONSOMMÉE PAR LE CHEMIN D'ARCHIVE depuis XFILE-65 : celui-ci a désormais ses propres
 * bornes ({@link ARCHIVE_SELECTION_FOLDERS_MAX}). La constante SURVIT parce que
 * {@link BULK_FOLDERS_MAX} s'y adosse explicitement dans sa justification — le raisonnement « un
 * dossier n'est pas une ligne mais un SOUS-ARBRE » reste juste POUR LES OPÉRATIONS D'ÉCRITURE, qui
 * sont les seules à l'appliquer aujourd'hui. La retirer casserait cet ancrage documentaire (et
 * l'export du paquet `@xfile/shared`) sans rien gagner.
 */
export const BULK_ARCHIVE_FOLDERS_MAX = 200;
/**
 * Bornes DÉDIÉES à l'archive ZIP d'une SÉLECTION (XFILE-65) — bien plus HAUTES que celles des
 * opérations groupées ({@link BULK_FILES_MAX} = 1 000, {@link BULK_FOLDERS_MAX} = 200).
 *
 * POURQUOI LE ZIP PEUT SE LE PERMETTRE ALORS QUE LES AUTRES NON. Ce n'est pas un assouplissement de
 * confort, c'est une différence de NATURE entre les deux chemins :
 *
 *  - `bulk-delete` / `bulk-move` / le préflight par lot / le retrait par le déposant anonyme
 *    ÉCRIVENT en base, dans une TRANSACTION. Le coût y est un verrou tenu : plus le lot est gros,
 *    plus la transaction est longue, plus elle bloque les autres écrivains et gonfle le travail de
 *    ROLLBACK si elle échoue. La borne y est un plafond de DURÉE DE VERROU, pas de mémoire.
 *  - L'archive est en LECTURE SEULE et STREAMÉE : aucune écriture, aucune transaction, aucun verrou.
 *    Les entrées sont émises au fil de l'eau (`yazl` en mode STORED, blobs lus en flux disque), donc
 *    la mémoire du serveur ne croît pas avec la taille de la sélection. Un abandon en cours de
 *    téléchargement ne laisse RIEN à défaire — la connexion se ferme, c'est tout. Le seul coût qui
 *    croît est le temps de transfert, et il est déjà payé par le client qui l'a demandé.
 *
 * CE QUI MORD RÉELLEMENT EN PREMIER, mesuré et non supposé : le `bodyLimit` de Fastify (1 MiB par
 * défaut, NON surchargé dans ce projet). Un uuid JSON pèse 39 octets (36 + 2 guillemets + 1 virgule)
 * ⇒ 10 000 fichiers + 2 000 dossiers ≈ 457 Kio, soit 44 % du plafond. Le 400 du schéma tombe donc
 * TOUJOURS avant le 413 du parseur, et l'utilisateur reçoit un message intelligible. Le point de
 * bascule se situe vers 26 900 uuid : c'est le vrai plafond à ne pas franchir sans relever
 * explicitement `bodyLimit`. Deux tests de `bulk-archive.test.ts` verrouillent cette marge.
 */
export const ARCHIVE_SELECTION_FILES_MAX = 10000;
/** Voir {@link ARCHIVE_SELECTION_FILES_MAX} — même raisonnement, côté dossiers. */
export const ARCHIVE_SELECTION_FOLDERS_MAX = 2000;
/**
 * Corps de `POST /api/archive/estimate` ET `POST /api/archive/prepare` (XFILE-27) : une SÉLECTION à
 * archiver en un seul ZIP, composée de fichiers isolés (`fileIds`, à la racine du ZIP) et/ou de
 * dossiers (`folderIds`, inclus comme SOUS-ARBRES récursifs). Chaque liste est bornée (fichiers ≤
 * {@link ARCHIVE_SELECTION_FILES_MAX}, dossiers ≤ {@link ARCHIVE_SELECTION_FOLDERS_MAX}) et vaut `[]`
 * par défaut ; la sélection doit contenir AU MOINS un élément (fichier ou dossier), sinon 400. Le
 * serveur contrôle le DROIT DE LECTURE de chaque id fourni (403 si non lisible, 404 si inexistant).
 *
 * ⚠ CES BORNES SONT PROPRES À L'ARCHIVE, et il ne faut pas les confondre avec {@link BULK_FILES_MAX} :
 * ce dernier borne des opérations d'ÉCRITURE TRANSACTIONNELLES (effacement/déplacement groupés,
 * préflight, retrait par le déposant anonyme) et doit rester bas. Le motif complet est en docstring
 * d'{@link ARCHIVE_SELECTION_FILES_MAX}.
 */
export const bulkArchiveRequestSchema = z
    .object({
    fileIds: z.array(z.uuid()).max(ARCHIVE_SELECTION_FILES_MAX).default([]),
    folderIds: z.array(z.uuid()).max(ARCHIVE_SELECTION_FOLDERS_MAX).default([]),
})
    .refine((v) => v.fileIds.length + v.folderIds.length >= 1, {
    message: 'Sélection vide : au moins un fichier ou dossier requis',
});
/**
 * Réponse de `POST /api/archive/prepare` (XFILE-27) : un JETON OPAQUE à COURTE DURÉE DE VIE (≈ 2 min)
 * liant l'utilisateur appelant à sa sélection validée. Le client le passe ensuite à
 * `GET /api/archive.zip?token=…&slot=N` (téléchargement browser-natif). Le jeton n'est PAS un secret
 * durable : c'est un nonce de téléchargement (une map en mémoire côté serveur le résout puis l'oublie).
 */
export const bulkArchivePrepareResultSchema = z.object({ token: z.string() });
/**
 * Segments de rangement automatique année/mois d'une date (XFILE-02) : `AAAA` et
 * `MM` (mois zéro-paddé), en UTC. Helper PUR (source unique front + back, testable)
 * — la résolution/création des dossiers `auto` correspondants se fait côté serveur,
 * et sera branchée sur l'upload en T4.
 */
export function yearMonthSegments(date) {
    const year = String(date.getUTCFullYear()).padStart(4, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    return { year, month };
}
/**
 * Contrats d'UPLOAD (XFILE-03). L'upload est une requête `multipart/form-data` :
 *  - un champ `ttlKind` (`TtlKind`, défaut `1m`) appliqué à TOUS les fichiers du lot ;
 *  - un champ optionnel `folderId` (uuid) — destination explicite (dossier courant côté
 *    admin) ; ABSENT ⇒ rangement automatique année/mois (XFILE-02) ;
 *  - une ou plusieurs parties « fichier ».
 * La réponse liste les fichiers écrits (`files`) et ceux ignorés (`skipped`, non
 * bloquants). Le contenu est adressé par UUID shardé hors base (ADR-0002) — la clé
 * n'est jamais exposée.
 */
/** Motif d'ignorance d'un fichier à l'upload (non bloquant pour les autres du lot). */
export const uploadSkipReasonSchema = z.enum(['too_large', 'empty', 'bad_name', 'write_error']);
/** Un fichier écrit : le nœud fichier + le dossier de destination effectif. */
export const uploadedFileSchema = fileNodeSchema.extend({
    folderId: z.uuid().nullable(),
});
/** Un fichier refusé à l'upload, avec son motif. */
export const uploadSkippedSchema = z.object({
    name: z.string(),
    reason: uploadSkipReasonSchema,
});
/**
 * Réponse de `POST /api/files/upload` : fichiers écrits + fichiers ignorés + le LIEN
 * DE PARTAGE du lot (XFILE-06 ; ADR-0005). `share` est disponible DÈS l'upload :
 *  - un SEUL fichier écrit → lien FICHIER (`targetKind: 'file'`) ;
 *  - PLUSIEURS fichiers écrits → sous-dossier de lot créé automatiquement + lien
 *    DOSSIER (`targetKind: 'folder'`) vers ce lot ;
 *  - aucun fichier écrit (tous ignorés) → `share: null`.
 */
export const uploadResultSchema = z.object({
    files: z.array(uploadedFileSchema),
    skipped: z.array(uploadSkippedSchema),
    share: shareLinkSchema.nullable(),
});
/**
 * Contrats d'UPLOAD FICHIER-PAR-FICHIER & de PRÉFLIGHT (Lot E) — source unique front + back.
 *
 * Le dépôt web devient FICHIER-PAR-FICHIER (une requête HTTP par fichier) pour permettre
 * progression + reprise, et IDEMPOTENT par HASH : avant de transférer les octets d'un fichier,
 * le client interroge le préflight avec le HASH SHA-256 du contenu. Le serveur peut alors :
 *  - reconnaître un DOUBLON exact déjà rangé là (rien à faire) ;
 *  - reconnaître un CONTENU déjà stocké (dédup) et créer la ligne par RÉFÉRENCE, sans transfert ;
 *  - sinon demander le transfert des octets.
 * L'upload est À PLAT : les fichiers atterrissent directement dans le dossier de destination
 * (aucune reconstruction d'arborescence — le client gère les dossiers et leur partage).
 */
/**
 * HASH SHA-256 d'un contenu, en hexadécimal MINUSCULE (64 caractères) — identité de contenu
 * partagée par l'idempotence (préflight) et la vérification d'intégrité (upload par-fichier).
 * Le client hashe les octets QU'IL ENVOIE ; le serveur hashe les octets REÇUS (avant toute
 * optimisation d'image) et compare — c'est un contrôle de TRANSFERT, pas de stockage.
 */
export const contentHashSchema = z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'Hash SHA-256 hexadécimal (64 caractères minuscules) attendu');
/**
 * Corps de `POST /api/uploads/preflight` (Lot E) : décrit UN fichier candidat (destination, nom,
 * hash de contenu, taille annoncée, classe de TTL). Le serveur valide `folderId` (dossier actif)
 * et répond par l'un des trois statuts de {@link uploadPreflightResultSchema}.
 */
export const uploadPreflightRequestSchema = z.object({
    folderId: z.uuid(),
    name: entryNameSchema,
    contentHash: contentHashSchema,
    sizeBytes: z.number().int().nonnegative(),
    ttlKind: ttlKindSchema,
});
/**
 * Réponse de `POST /api/uploads/preflight` : UNION DISCRIMINÉE sur `status` :
 *  - `duplicate`    : un fichier ACTIF de même `(folderId, name, contentHash)` existe déjà — on
 *                     renvoie ce fichier (le client n'a RIEN à transférer) ;
 *  - `linked`       : le contenu (`contentHash`) est déjà stocké (blob présent) — le serveur a
 *                     CRÉÉ une ligne fichier par RÉFÉRENCE (copie = référence, sans transfert) et
 *                     renvoie le fichier créé ;
 *  - `needs_upload` : contenu inédit — le client doit transférer les octets via `POST
 *                     /api/uploads/file`.
 */
export const uploadPreflightResultSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('duplicate'), file: uploadedFileSchema }),
    z.object({ status: z.literal('linked'), file: uploadedFileSchema }),
    z.object({ status: z.literal('needs_upload') }),
]);
/**
 * Réponse de `POST /api/uploads/file` (Lot E) : le fichier RÉELLEMENT créé (upload d'UN seul
 * fichier, à plat dans le dossier de destination). Aucun lien de partage n'est créé ici — le
 * client gère le partage du dossier de lot.
 */
export const oneFileUploadResultSchema = z.object({ file: uploadedFileSchema });
/**
 * Réponse de `POST /api/uploads/auto-folder` (Lot E) : l'id d'un NOUVEAU sous-dossier de lot,
 * matérialisé sous les dossiers `auto` `AAAA/MM` du jour. Le client y dépose tout le lot puis
 * crée le lien de partage du dossier.
 */
export const autoFolderResultSchema = z.object({ folderId: z.uuid() });
/**
 * Tailles de VIGNETTE INTERNES (whitelist STRICTE des tailles GÉNÉRÉES à l'upload et servies à
 * l'explorateur/visionneuse AUTHENTIFIÉ) : `sm` (160 px), `md` (400 px) et `lg` (1600 px), côté le
 * plus long. Toute autre valeur est refusée (empêche la génération infinie de variantes). La grande
 * vignette `lg` alimente la VISIONNEUSE web (XFILE-26) — une WebP ~q80 bornée à 1600 px, assez
 * détaillée pour l'aperçu plein écran sans servir l'original.
 *
 * ⚠ La route PUBLIQUE pnyx (`GET /f/:slug/vignette/:size`) N'EXPOSE QUE `sm`/`md` (contrat pnyx §3) :
 * elle valide via {@link publicThumbnailSizeSchema}, qui REJETTE `lg` (400). `lg` reste donc interne.
 */
export const THUMBNAIL_SIZES = ['sm', 'md', 'lg'];
export const thumbnailSizeSchema = z.enum(THUMBNAIL_SIZES);
/** Côté le plus long (px) de chaque taille de vignette interne. */
export const THUMBNAIL_MAX_PX = { sm: 160, md: 400, lg: 1600 };
/**
 * Tailles de vignette EXPOSÉES PUBLIQUEMENT via le lien de partage pnyx (`GET /f/:slug/vignette/:size`,
 * contrat pnyx §3) : STRICTEMENT `sm`/`md`. Sous-ensemble de {@link THUMBNAIL_SIZES}. La grande
 * vignette `lg` demeure INTERNE (explorateur/visionneuse authentifié) et n'est JAMAIS servie sur la
 * route publique — `publicThumbnailSizeSchema` la rejette (400), préservant le contrat pnyx même si
 * l'enum `thumbnailSizeSchema` (partagé par la route authentifiée) accepte désormais `lg`.
 */
export const PUBLIC_THUMBNAIL_SIZES = ['sm', 'md'];
export const publicThumbnailSizeSchema = z.enum(PUBLIC_THUMBNAIL_SIZES);
/**
 * Dimensions (px) d'un contenu image : `w`/`h`, ou `null` pour un non-image (ou dimensions
 * indéterminées). Réutilisé par l'upload pnyx et la meta.
 */
export const dimensionsSchema = z
    .object({ w: z.number().int(), h: z.number().int() })
    .nullable();
/**
 * Réponse RICHE de l'upload proxifié pnyx (`POST /api/community/deposit/:token/upload`,
 * contrat pnyx §1) — évite un second appel `meta`. `id` = le slug de partage xfile
 * (`/f/:id`). Défini ici (phase 1) ; câblé côté route en phase 3.
 */
export const pnyxUploadResultSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimetype: z.string(),
    size: z.number().int().nonnegative(),
    dimensions: dimensionsSchema,
    has_thumbnail: z.boolean(),
    provenance: provenanceSchema,
});
/**
 * Métadonnées d'un fichier partagé (`GET /f/:id/meta`, contrat pnyx §4). `id` = le slug de
 * partage xfile. `dimensions` = `null` pour un non-image. `created_at` = ISO.
 */
export const fileMetaSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimetype: z.string(),
    size: z.number().int().nonnegative(),
    dimensions: dimensionsSchema,
    has_thumbnail: z.boolean(),
    created_at: z.string(),
    provenance: provenanceSchema,
});
// ---------------------------------------------------------------------------
// LOT 1 — FONDATIONS. Contrats posés ici, consommés par les lots suivants.
//
// Tous STRICTEMENT ADDITIFS : aucun champ existant n'est retiré ni rendu obligatoire, pour que le
// gate reste vert entre les lots. Chaque bloc indique QUEL LOT le consommera.
// ---------------------------------------------------------------------------
/**
 * PRÉFLIGHT PAR LOT — corps de `POST /api/uploads/preflight/batch`. **Consommé par le LOT 2.**
 *
 * POURQUOI. Le préflight actuel ({@link uploadPreflightRequestSchema}) est UNITAIRE : le cas de
 * référence du produit — 618 fichiers / 450 Mo (v1.10.0) — coûte 618 requêtes HTTP et ~3 000 allers
 * SQL avant que le moindre octet ne parte. Or le préflight est PUREMENT décisionnel (existe-t-il
 * déjà ? le contenu est-il déjà stocké ?) : rien n'impose une requête par fichier.
 *
 * `folderId` et `ttlKind` sont FACTORISÉS hors des items (une file d'attente vise une seule
 * destination avec une seule durée de conservation) ; seuls varient nom, hash et taille. Le lot est
 * borné par {@link BULK_FILES_MAX}, comme les autres opérations groupées.
 *
 * ⚠ L'endpoint UNITAIRE RESTE EN PLACE, et ce n'est pas de la compatibilité descendante gratuite :
 * c'est le chemin de REPRISE. Après un rechargement d'onglet, le moteur d'upload rejoue le préflight
 * fichier par fichier pour sauter les déjà-déposés — il a besoin de la forme unitaire.
 */
export const uploadPreflightBatchItemSchema = z.object({
    name: entryNameSchema,
    contentHash: contentHashSchema,
    sizeBytes: z.number().int().nonnegative(),
});
export const uploadPreflightBatchRequestSchema = z.object({
    folderId: z.uuid(),
    ttlKind: ttlKindSchema,
    items: z.array(uploadPreflightBatchItemSchema).min(1).max(BULK_FILES_MAX),
});
/**
 * Réponse du préflight par lot : un statut PAR ITEM, dans l'ORDRE EXACT de la requête.
 *
 * L'alignement positionnel est le contrat : `results[i]` répond à `items[i]`. On ne ré-identifie pas
 * par `contentHash` — un même contenu peut apparaître DEUX FOIS dans un lot sous deux noms
 * différents (c'est même le cas normal de la dédup), et deux entrées porteraient alors le même hash.
 * Le serveur DOIT donc renvoyer autant de résultats que d'items ; le client refuse un lot de
 * longueur différente plutôt que de deviner.
 *
 * Chaque statut est la MÊME union discriminée que l'unitaire ({@link uploadPreflightResultSchema}) :
 * `duplicate` / `linked` / `needs_upload`. Réutilisée telle quelle, sans variante — les deux
 * endpoints doivent rester interprétables par le même code client.
 */
export const uploadPreflightBatchResultSchema = z.object({
    results: z.array(uploadPreflightResultSchema),
});
/**
 * RENOMMAGE D'UN FICHIER — corps de `PATCH /api/files/:id`. **Consommé par le LOT 2.**
 *
 * MANQUE COMBLÉ : aucune route ne renomme un fichier aujourd'hui (seuls les DOSSIERS le sont, via
 * {@link updateFolderRequestSchema}). Un nom de fichier malheureux ne se corrige qu'en supprimant et
 * redéposant — ce qui change l'id, donc CASSE le lien de partage. C'est une asymétrie, pas un choix.
 *
 * Forme ALIGNÉE sur `updateFolderRequestSchema` (champ optionnel, `name` validé par
 * {@link entryNameSchema}) pour que les deux routes de renommage se ressemblent côté client. On
 * n'expose PAS `folderId` ici : le déplacement d'un fichier a déjà son contrat groupé
 * ({@link bulkMoveFilesRequestSchema}), et deux chemins pour la même opération se désynchronisent.
 *
 * Le renommage NE CASSE AUCUN lien de partage (les `share_links` référencent l'id, jamais le nom) —
 * même garantie que pour les dossiers. Collision de nom entre frères actifs → 409 côté serveur.
 */
export const updateFileRequestSchema = z.object({
    name: entryNameSchema.optional(),
});
/**
 * Borne du nombre de DOSSIERS d'une opération groupée. **Consommée par le LOT 2.**
 *
 * Bien plus BASSE que {@link BULK_FILES_MAX} (1 000), et pour une raison de fond : un dossier n'est
 * pas une ligne, c'est un SOUS-ARBRE. Chaque id déclenche une CTE récursive (suppression récursive,
 * calcul d'impact), donc le coût est proportionnel au contenu, pas au nombre d'ids. On s'aligne sur
 * {@link BULK_ARCHIVE_FOLDERS_MAX} (200), déjà retenue pour l'archive de sélection, exactement pour
 * ce motif.
 */
export const BULK_FOLDERS_MAX = 200;
/**
 * SUPPRESSION GROUPÉE DE DOSSIERS — corps de `POST /api/folders/bulk-delete`. **LOT 2.**
 *
 * MANQUE COMBLÉ : les fichiers ont `bulk-delete`/`bulk-move`, les dossiers non. Le web compense par
 * une BOUCLE SÉQUENTIELLE d'appels unitaires — 20 dossiers = 21 allers-retours — et, surtout, cette
 * boucle produit 20 estampilles de lot DISTINCTES : l'annulation n'est alors plus une action mais 20,
 * et si l'une échoue la corbeille reste à moitié restaurée. Ce n'est pas un problème de performance,
 * c'est un problème d'ATOMICITÉ de l'annulation.
 */
export const bulkDeleteFoldersRequestSchema = z.object({
    ids: z.array(z.uuid()).min(1).max(BULK_FOLDERS_MAX),
});
/**
 * Réponse de `POST /api/folders/bulk-delete` : les compteurs des DEUX natures réellement basculées
 * (un dossier emporte son sous-arbre : sous-dossiers ET fichiers), plus **UNE SEULE** estampille.
 *
 * `batch` UNIQUE est la raison d'être de ce contrat : un seul uuid couvrant tout le geste, donc
 * `POST /api/deletions/:batch/restore` en UNE requête, et un toaster « Annuler » qui reste un bouton.
 * OPTIONNEL et ABSENT quand rien n'a basculé (`folders === 0 && files === 0`) — il n'y a alors rien
 * à annuler. Même convention que {@link bulkDeleteFilesResultSchema}.
 */
export const bulkDeleteFoldersResultSchema = z.object({
    folders: z.number().int().nonnegative(),
    files: z.number().int().nonnegative(),
    batch: z.uuid().optional(),
});
/**
 * DÉPLACEMENT GROUPÉ DE DOSSIERS — corps de `POST /api/folders/bulk-move`. **LOT 2.**
 *
 * `parentId: null` = déplacement vers la racine, même convention que
 * {@link updateFolderRequestSchema}. Le serveur applique à CHAQUE id les gardes déjà en vigueur pour
 * le déplacement unitaire : anti-cycle (dans soi-même ou un descendant → 400), dossier `auto`
 * immuable (403), collision de nom entre frères (409).
 */
export const bulkMoveFoldersRequestSchema = z.object({
    ids: z.array(z.uuid()).min(1).max(BULK_FOLDERS_MAX),
    parentId: z.uuid().nullable(),
});
/** Réponse de `POST /api/folders/bulk-move` : nombre de dossiers ACTIFS effectivement déplacés. */
export const bulkMoveFoldersResultSchema = z.object({
    moved: z.number().int().nonnegative(),
});
/**
 * IMPACT D'EFFACEMENT GROUPÉ — corps de `POST /api/folders/bulk-delete-impact`. **LOT 2.**
 *
 * POURQUOI. La modale de confirmation chiffre aujourd'hui l'impact via
 * `GET /api/folders/:id/delete-impact`, UNE REQUÊTE PAR DOSSIER — soit, sur une sélection, jusqu'à
 * 25 CTE récursives CONCURRENTES lancées depuis un navigateur (borne posée en v2.2.0 précisément
 * pour contenir la casse). Un seul appel agrège côté serveur, en une passe sur l'union des
 * sous-arbres, ce qui règle AUSSI le double comptage : deux dossiers sélectionnés dont l'un
 * descend de l'autre ne doivent PAS additionner leurs contenus.
 *
 * `fileIds` est présent parce qu'une sélection réelle MÊLE dossiers et fichiers isolés : l'impact
 * annoncé doit couvrir tout ce que le bouton « Supprimer » va emporter, pas seulement les dossiers.
 */
export const bulkDeleteImpactRequestSchema = z
    .object({
    folderIds: z.array(z.uuid()).max(BULK_FOLDERS_MAX).default([]),
    fileIds: z.array(z.uuid()).max(BULK_FILES_MAX).default([]),
})
    .refine((v) => v.folderIds.length + v.fileIds.length >= 1, {
    message: 'Sélection vide : au moins un dossier ou fichier requis',
});
/**
 * Réponse de l'impact groupé. MÊME FORME que {@link folderDeleteImpactSchema} — délibérément, pour
 * que la modale de confirmation ait UN seul rendu à écrire, qu'elle vise un dossier ou une sélection.
 *
 * Sémantique des compteurs, sur l'UNION DÉDUPLIQUÉE des sous-arbres (jamais une somme d'appels
 * unitaires) : `folders` = sous-dossiers descendants, les dossiers sélectionnés EXCLUS (ils sont déjà
 * énoncés par la sélection) ; `depth` = profondeur maximale atteinte ; `files` = fichiers actifs de
 * toute l'union, `fileIds` inclus ; `ttl` = agrégat de durée de vie de ces mêmes fichiers.
 * Comme pour l'impact unitaire, `files` ne filtre PAS sur `storage_key` : on annonce ce qui sera
 * EFFACÉ, pas ce qui serait zippable.
 */
export const bulkDeleteImpactSchema = folderDeleteImpactSchema;
/**
 * CORBEILLE — un LOT de suppression restaurable, tel que listé par `GET /api/deletions`. **LOT 2.**
 *
 * MANQUE COMBLÉ, et c'est le plus asymétrique du produit : `deleted_batch` (migration 0018) rend
 * chaque suppression restaurable pendant **30 jours** (jusqu'à la purge des soft-deleted), mais RIEN
 * NE LISTE les lots effacés. La seule affordance d'annulation est le bouton du toaster, qui vit
 * **8 secondes**. Passé ce délai, la donnée reste parfaitement restaurable et devient
 * inatteignable — un écart de quatre ordres de grandeur entre ce que le modèle garantit et ce que
 * l'interface offre. `POST /api/deletions/:batch/restore` existe déjà et ne change pas : il ne
 * manquait que de quoi retrouver le `batch`.
 *
 * `path` est le chemin d'ORIGINE reconstitué (« où était-ce ? »), sans quoi une liste de lots
 * horodatés est indéchiffrable. `actorEmail` est OPTIONNEL : l'acteur peut être un compte depuis
 * supprimé, ou une suppression anonyme par le déposant (jeton de gestion, XFILE-37).
 * `restorable` dit si le lot peut ENCORE être restauré : un lot dont le parent a disparu, ou dont un
 * nom entre en collision, échoue en 409 — mieux vaut griser le bouton que promettre puis refuser.
 */
export const deletionBatchEntrySchema = z.object({
    batch: z.uuid(),
    deletedAt: z.string(),
    actorId: z.uuid().nullable(),
    actorEmail: z.string().nullable(),
    folders: z.number().int().nonnegative(),
    files: z.number().int().nonnegative(),
    path: z.string().nullable(),
    restorable: z.boolean(),
});
/**
 * Réponse de `GET /api/deletions` : les lots restaurables, du plus RÉCENT au plus ancien, paginés
 * par CURSEUR, comme le journal d'actions — et pas par offset : une purge concurrente décale les
 * pages d'un offset et fait sauter des lots. `nextBefore` absent = fin de liste.
 *
 * ⚠ `nextBefore` est un CURSEUR OPAQUE, et non plus la date du dernier lot rendu : il porte le
 * couple `<ISO>|<uuid>`, sans quoi des lots partageant la même `deleted_at` à la milliseconde près
 * étaient sautés ou rendus deux fois d'une page à l'autre (une suppression groupée estampille tout
 * un sous-arbre dans UNE transaction, donc à un `now()` unique). Le client le RENVOIE TEL QUEL en
 * `?before=`, sans jamais le parser ni le reconstruire. Le serveur accepte encore l'ANCIENNE forme
 * (horodatage seul) pour ne pas casser un onglet resté ouvert pendant un déploiement.
 *
 * ⚠ `nextBefore` PEUT accompagner une page VIDE. Le filtre de droits s'applique après la
 * pagination : quand le balayage borné du serveur n'a rencontré que des lots d'autrui, la page est
 * vide mais la liste ne l'est PAS — le curseur est alors le seul moyen d'atteindre la suite. Traiter
 * « page vide » comme « fin de liste » rendrait ses propres lots inatteignables.
 */
export const deletionBatchListSchema = z.object({
    batches: z.array(deletionBatchEntrySchema),
    nextBefore: z.string().optional(),
});
/**
 * Un FICHIER d'un lot de suppression (`GET /api/deletions/:batch/items`, XFILE-75) — de quoi
 * l'afficher et le SERVIR sans le restaurer. `path` = chemin RELATIF dans le lot (« dossier /
 * sous-dossier / nom.ext » pour un fichier imbriqué, nom simple pour un fichier déposé à la racine du
 * lot). `hasContent` = le contenu est encore présent (`storage_key IS NOT NULL AND purged_at IS
 * NULL`) : à `false`, la GC a déjà purgé le blob et le fichier n'est ni consultable ni restaurable en
 * l'état. `hasThumbnail` autorise la galerie. La clé de stockage interne n'est JAMAIS exposée.
 */
export const deletionBatchItemFileSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    mimeType: z.string(),
    hasContent: z.boolean(),
    hasThumbnail: z.boolean(),
    path: z.string(),
});
/**
 * Un DOSSIER d'un lot de suppression (`GET /api/deletions/:batch/items`, XFILE-75). `path` = chemin
 * RELATIF dans le lot (nom simple pour une racine du lot, chemin pour un sous-dossier).
 */
export const deletionBatchItemFolderSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    path: z.string(),
});
/**
 * Réponse de `GET /api/deletions/:batch/items` (XFILE-75) : le CONTENU d'un lot de suppression, pour
 * que le web puisse DÉPLIER un lot (onglet « Supprimés ») et y consulter/télécharger les fichiers via
 * `GET /api/deletions/file/:fileId/content` et `…/thumbnail/:size`. 404 côté route si le lot est
 * inconnu (aucune ligne). Chemins RELATIFS au lot (motif `rel_path` de l'archive de dossier).
 */
export const deletionBatchItemsSchema = z.object({
    files: z.array(deletionBatchItemFileSchema),
    folders: z.array(deletionBatchItemFolderSchema),
});
// ---------------------------------------------------------------------------
// CORBEILLE DES EXPIRÉS (XFILE-74) — les fichiers échus par TTL entrent en corbeille.
//
// Un TTL qui échoit ne PURGE PLUS le contenu immédiatement : le fichier ENTRE EN CORBEILLE, son
// contenu y est retenu {@link EXPIRED_TRASH_RETENTION_DAYS} jours, puis purgé. Un admin peut le
// RESTAURER pendant cette fenêtre. Contrairement aux suppressions manuelles (groupées par
// `deleted_batch`, restaurées EN BLOC à leur emplacement d'origine), les expirés sont INDIVIDUELS
// et restaurés AU CAS PAR CAS vers UN dossier de destination choisi.
//
// Ces deux corbeilles sont DISJOINTES par construction : une expiration pose `expired_at` et
// JAMAIS `deleted_batch`, une suppression manuelle l'inverse. `GET /api/deletions` ne voit que les
// secondes, `GET /api/deletions/expired` que les premières.
// ---------------------------------------------------------------------------
/**
 * Nombre de JOURS pendant lesquels le contenu d'un fichier EXPIRÉ par TTL est retenu en corbeille
 * avant d'être purgé (XFILE-74). Fenêtre DÉDIÉE, distincte des 30 j de restauration d'une
 * suppression manuelle et du délai de grâce d'annulation. Consommée côté serveur par la GC
 * (`FileRepo.listPurgeable`) ; exposée au web pour l'affichage.
 */
export const EXPIRED_TRASH_RETENTION_DAYS = 7;
/**
 * Classe de TTL RÉATTRIBUÉE à un fichier expiré RESTAURÉ (XFILE-74) : il repart avec 1 JOUR de
 * sursis (`expires_at = expiresAtFor('1d', now)`), à charge pour l'utilisateur de le PROLONGER
 * ensuite via l'existant. Volontairement court : restaurer remet en ligne, cela ne décide pas d'une
 * nouvelle conservation longue.
 */
export const EXPIRED_RESTORE_TTL_KIND = '1d';
/**
 * Une entrée de la corbeille des EXPIRÉS (`GET /api/deletions/expired`, XFILE-74) — un fichier
 * INDIVIDUEL échu par TTL, dont le contenu est encore présent (retenu ≤ {@link
 * EXPIRED_TRASH_RETENTION_DAYS} jours). `path` est le chemin d'ORIGINE reconstitué (le dossier
 * CONTENANT, remonté jusqu'à la racine d'espace, comme le `path` de {@link deletionBatchEntrySchema})
 * — AFFICHÉ uniquement, la restauration se fait vers un dossier CHOISI, jamais réinjectée à cet
 * emplacement. `originFolderId` = l'id de ce dossier (pour info). La clé de stockage interne n'est
 * JAMAIS exposée.
 */
export const expiredFileEntrySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    mimeType: z.string(),
    provenance: provenanceSchema,
    hasThumbnail: z.boolean(),
    expiredAt: z.string(),
    path: z.string().nullable(),
    originFolderId: z.uuid().nullable(),
});
/**
 * Réponse de `GET /api/deletions/expired` : les fichiers expirés, du plus RÉCEMMENT expiré au plus
 * ancien, paginés par CURSEUR OPAQUE. `nextBefore` absent = fin de liste.
 *
 * ⚠ `nextBefore` est un CURSEUR OPAQUE composite (`<ISO µs>|<uuid>`), à renvoyer TEL QUEL en
 * `?before=` — jamais à parser ni reconstruire côté client. Comme la corbeille des suppressions, sa
 * moitié gauche est formatée à la MICROSECONDE côté serveur : un curseur bâti sur un `Date` JS
 * tronquerait le `timestamptz` et sauterait des fichiers de la même milliseconde.
 */
export const expiredFileListSchema = z.object({
    files: z.array(expiredFileEntrySchema),
    nextBefore: z.string().optional(),
});
/**
 * Corps de `POST /api/deletions/expired/restore` (XFILE-74) : restaure une SÉLECTION de fichiers
 * expirés vers UN dossier de destination COMMUN (`destFolderId`). Les fichiers repartent avec un TTL
 * de 1 jour ({@link EXPIRED_RESTORE_TTL_KIND}) et leur ancien lien de partage est RÉVOQUÉ — remettre
 * le fichier en ligne exige de le RE-PARTAGER (l'ancien slug reste 410). Borné à {@link
 * BULK_FILES_MAX}, comme les autres opérations groupées.
 */
export const restoreExpiredRequestSchema = z.object({
    fileIds: z.array(z.uuid()).min(1).max(BULK_FILES_MAX),
    destFolderId: z.uuid(),
});
/**
 * Réponse de `POST /api/deletions/expired/restore` : `restored` = fichiers effectivement remis en
 * ligne, `skipped` = ids demandés IGNORÉS (déjà purgés, inconnus, ou plus en corbeille des expirés).
 * L'opération est idempotente en ce sens : rejouer sur des ids déjà restaurés les compte en `skipped`.
 */
export const restoreExpiredResultSchema = z.object({
    restored: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// PURGE DÉFINITIVE depuis la corbeille (XFILE-76) — suppression IRRÉVERSIBLE.
//
// La corbeille (admin) rendait restaurable ; ces contrats en font le pendant DESTRUCTEUR : purger un
// LOT, purger des FICHIERS choisis (sert l'onglet Expirés), ou VIDER tout le soft-deleted avant une
// date. Toutes ces opérations ne touchent QUE des lignes soft-deleted (`deleted_at IS NOT NULL`) :
// jamais un élément actif. Elles suivent le motif éprouvé de `MaintenanceRepo.purgeSoftDeleted`
// (content-first, dédup-aware, FK-sûr). Réservées aux `admin`/`superadmin` côté route.
// ---------------------------------------------------------------------------
/**
 * Comptes de lignes DÉFINITIVEMENT purgées (`files`/`folders`) — réponse de
 * `POST /api/deletions/:batch/purge` et `POST /api/deletions/purge-before` (XFILE-76). `folders` peut
 * être inférieur au nombre de dossiers du lot/de la fenêtre si un dossier reste référencé par un
 * fichier subsistant (épargné ce tour-ci, purgé au balayage suivant) — même sémantique que la purge
 * des soft-deleted > 30 j.
 */
export const purgeCountsResultSchema = z.object({
    files: z.number().int().nonnegative(),
    folders: z.number().int().nonnegative(),
});
/**
 * Corps de `POST /api/deletions/files/purge` (XFILE-76) : purge DÉFINITIVE d'une sélection de fichiers
 * EN CORBEILLE (expirés OU supprimés manuellement), désignés par leurs ids (1..{@link BULK_FILES_MAX}).
 * Un id ACTIF ou inconnu passé dans la liste est IGNORÉ (jamais purgé) — idempotence.
 */
export const purgeFilesRequestSchema = z.object({
    fileIds: z.array(z.uuid()).min(1).max(BULK_FILES_MAX),
});
/**
 * Réponse de `POST /api/deletions/files/purge` : `purged` = fichiers réellement effacés
 * définitivement, `skipped` = ids demandés IGNORÉS (actifs, inconnus, ou déjà purgés). L'appelant
 * dé-doublonne les ids, donc `skipped = ids uniques - purged` est exact.
 */
export const purgeFilesResultSchema = z.object({
    purged: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
});
/**
 * Borne composite (corps de `POST /api/deletions/purge-before` + query de son aperçu, XFILE-76) :
 * purge TOUT le soft-deleted (LES DEUX corbeilles — expirés ET suppressions manuelles ont tous
 * `deleted_at`) dont `deleted_at < before`. `before` est un datetime ISO 8601 (le web mappe son
 * sélecteur de date, défaut « aujourd'hui → tout vider », vers une fin de journée). `offset: true`
 * accepte aussi bien `…Z` qu'un décalage `+02:00`.
 */
export const purgeBeforeRequestSchema = z.object({
    before: z.iso.datetime({ offset: true }),
});
/**
 * Réponse de `GET /api/deletions/purge-before/preview?before=<datetime>` (XFILE-76) : l'IMPACT chiffré
 * du vidage, pour une confirmation honnête AVANT d'agir. `files`/`folders` = lignes soft-deleted
 * `deleted_at < before` ; `bytes` = somme des tailles des FICHIERS concernés dont le contenu subsiste
 * encore sur disque (`storage_key IS NOT NULL AND purged_at IS NULL`) — les octets que la purge
 * libérerait réellement. Ne supprime rien.
 */
export const purgeBeforePreviewSchema = z.object({
    files: z.number().int().nonnegative(),
    folders: z.number().int().nonnegative(),
    bytes: z.number().int().nonnegative(),
});
//# sourceMappingURL=storage.js.map